import { useEffect, useRef, useState } from 'react'
import type { Appointment, TaskListItem } from '@myapp/contract'
import { isSameDay } from '../../../../shared/helpers/date'

/**
 * A PLANNED item whose planned window (travel + work) ended earlier today. Only same-day items
 * count: yesterday's leftovers are `isStaleInProgress`'s case, and tomorrow's are simply future.
 */
export function isOverduePlanned(
  status: string,
  plannedStart: string | null | undefined,
  travel: number,
  work: number,
  now: Date,
): boolean {
  if (status !== 'PLANNED' || !plannedStart) return false
  if (!isSameDay(plannedStart, now)) return false
  const end = new Date(plannedStart).getTime() + (travel + work) * 60_000
  return end < now.getTime()
}

/** Started on an earlier day and never closed — still PLANNED with an actual start but no end. */
export function isStaleInProgress(
  status: string,
  actualStart: string | null | undefined,
  actualEnd: string | null | undefined,
  now: Date,
): boolean {
  if (status !== 'PLANNED') return false
  if (!actualStart || actualEnd) return false
  return !isSameDay(actualStart, now)
}

/**
 * Everything a Handwerker forgot to close, and the reminder modal built on top of it.
 *
 * The modal opens itself once items exist and stays dismissed for the session once closed —
 * `dismissedRef` survives the poll-driven re-renders that would otherwise re-open it every minute.
 * Clicking an item counts as dismissal too: the user is on their way to deal with it.
 */
export function useUnclosedItems(args: {
  appointments: Appointment[]
  tasks: TaskListItem[]
  onAppointmentClick: (appointmentId: number) => void
  onTaskClick: (taskId: number) => void
}) {
  const { appointments, tasks, onAppointmentClick, onTaskClick } = args

  const [show, setShowState] = useState(false)
  const dismissedRef = useRef(false)

  // Deliberately per render, not memoised: staleness must keep updating as polls re-render us.
  const now = new Date()

  const unclosedAppointments = appointments.filter(
    (a) =>
      isOverduePlanned(
        a.status,
        a.schedule.plannedWorkStartsAt,
        a.schedule.plannedTravelMinutes,
        a.schedule.plannedWorkMinutes,
        now,
      ) || isStaleInProgress(a.status, a.schedule.actualWorkStartsAt, a.schedule.actualEndsAt, now),
  )

  const unclosedTasks = tasks.filter(
    (t) =>
      isOverduePlanned(
        t.status,
        t.schedule?.plannedWorkStartsAt ?? undefined,
        t.schedule?.plannedTravelMinutes ?? 0,
        t.schedule?.plannedWorkMinutes ?? 0,
        now,
      ) ||
      isStaleInProgress(
        t.status,
        t.schedule?.actualWorkStartsAt ?? undefined,
        t.schedule?.actualEndsAt ?? undefined,
        now,
      ),
  )

  useEffect(() => {
    if (dismissedRef.current) return
    if (unclosedAppointments.length === 0 && unclosedTasks.length === 0) return
    setShowState(true)
  }, [unclosedAppointments.length, unclosedTasks.length])

  const setShow = (open: boolean) => {
    if (!open) {
      dismissedRef.current = true
      setShowState(false)
    }
  }

  const handleAppointmentClick = (id: number) => {
    dismissedRef.current = true
    setShowState(false)
    onAppointmentClick(id)
  }

  const handleTaskClick = (id: number) => {
    dismissedRef.current = true
    setShowState(false)
    onTaskClick(id)
  }

  return { unclosedAppointments, unclosedTasks, show, setShow, handleAppointmentClick, handleTaskClick }
}
